import type { SiteData } from '@/data/types';
import { resolveImagePath } from '@/utils/imageResolver';
import { cn } from '@/utils/cn';

interface HeaderProps {
  siteData: SiteData;
  className?: string;
}

export function Header({ siteData, className }: HeaderProps) {
  return (
    <header className={cn("bg-white shadow-sm", className)}>
      {/* Top contact bar */}
      <div className="bg-black text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center justify-end space-x-4 text-sm">
            <a 
              href={`tel:${siteData.contact.phone.replace(/[^0-9]/g, '')}`}
              className="hover:text-gray-300 transition-colors"
            >
              {siteData.contact.phone}
            </a>
            <span className="text-gray-400">|</span>
            <a 
              href={`mailto:${siteData.contact.email}`}
              className="hover:text-gray-300 transition-colors"
            >
              {siteData.contact.email}
            </a>
          </nav>
        </div>
      </div>

      {/* Main navigation */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <a href="/" className="flex items-center">
                <img
                  src={resolveImagePath(siteData.logo.dark)}
                  alt={`${siteData.title} Logo | Part 61 Flight Instruction School and Charter Flight Services in Chattanooga, Tennessee`}
                  className="h-9 w-auto"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {siteData.navigation.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
                >
                  {item.label}
                </a>
              ))}
              <a
                href="/contact-us"
                className="bg-blue-600 text-white px-6 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Contact Us
              </a>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                type="button"
                className="text-gray-700 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600"
                aria-controls="mobile-menu"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu, hidden by default */}
        <div className="md:hidden hidden" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
            {siteData.navigation.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="text-gray-700 hover:text-blue-600 block px-3 py-2 text-base font-medium"
              >
                {item.label}
              </a>
            ))}
            <a
              href="/contact-us"
              className="bg-blue-600 text-white block px-3 py-2 text-base font-medium rounded-md hover:bg-blue-700 transition-colors duration-200 mt-4"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}